// CSV Export Utility for Admin Data Tables

export interface CSVExportOptions {
  filename?: string
  includeTimestamp?: boolean
  excludeColumns?: string[]
  dateFormat?: 'iso' | 'locale'
}

export class CSVExporter {
  /**
   * Convert data array to CSV string
   */
  static convertToCSV(data: any[], options: CSVExportOptions = {}): string {
    if (!data.length) return ''
    
    const { excludeColumns = [], dateFormat = 'locale' } = options
    
    // Get all columns from the first row
    const allColumns = Object.keys(data[0])
    const columns = allColumns.filter(col => !excludeColumns.includes(col))
    
    // Create CSV header
    const header = columns.map(col => this.escapeCSVField(this.formatColumnName(col))).join(',')
    
    // Create CSV rows
    const rows = data.map(row => {
      return columns.map(col => {
        const value = row[col]
        return this.escapeCSVField(this.formatValue(value, col, dateFormat))
      }).join(',')
    })
    
    return [header, ...rows].join('\n')
  }
  
  /**
   * Download CSV file
   */
  static downloadCSV(data: any[], tableName: string, options: CSVExportOptions = {}): void {
    const { 
      filename = `${tableName}_export`,
      includeTimestamp = true
    } = options
    
    // Generate CSV content
    const csvContent = this.convertToCSV(data, options)
    
    // Create filename with timestamp
    const timestamp = includeTimestamp 
      ? `_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}`
      : ''
    const finalFilename = `${filename}${timestamp}.csv`
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', finalFilename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }
  
  /**
   * Format column names for CSV headers
   */
  private static formatColumnName(column: string): string {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  
  /**
   * Format cell values for CSV
   */
  private static formatValue(value: any, column: string, dateFormat: 'iso' | 'locale'): string {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return ''
    }
    
    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }
    
    // Handle dates
    if (column.includes('date') || column.includes('timestamp')) {
      try {
        const date = new Date(value)
        return dateFormat === 'iso' ? date.toISOString() : date.toLocaleString()
      } catch {
        return String(value)
      }
    }
    
    // Handle numbers (including costs)
    if (typeof value === 'number') {
      if (column.includes('cost')) {
        return value.toFixed(4)
      }
      return value.toString()
    }
    
    // Handle objects/arrays - convert to JSON
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value)
      } catch {
        return '[Object]'
      }
    }
    
    return String(value)
  }
  
  /**
   * Escape CSV field (handle commas, quotes, newlines)
   */
  private static escapeCSVField(field: string): string {
    if (!field) return ''
    
    const stringField = String(field)
    
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n') || stringField.includes('\r')) {
      return `"${stringField.replace(/"/g, '""')}"`
    }
    
    return stringField
  }
  
  /**
   * Get estimated file size
   */
  static getEstimatedSize(data: any[]): string {
    if (!data.length) return '0 B'
    
    const sampleCSV = this.convertToCSV(data.slice(0, Math.min(10, data.length)))
    const avgRowSize = sampleCSV.length / Math.min(10, data.length)
    const totalSize = avgRowSize * data.length
    
    if (totalSize < 1024) return `${Math.round(totalSize)} B`
    if (totalSize < 1024 * 1024) return `${Math.round(totalSize / 1024)} KB`
    return `${(totalSize / (1024 * 1024)).toFixed(1)} MB`
  }
}

export default CSVExporter